import React, { useState } from 'react';
import { Mail, MessageCircle, Clock, HelpCircle, Send, Phone, CheckCircle, ArrowRight, Sparkles, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function SupportPage() {
 const [selectedFaq, setSelectedFaq] = useState(null);
 const { t } = useTranslation();

 // FAQ数据配置
 const faqData = [
 {
 icon: '🔐',
 questionKey: 'login',
 color: 'blue'
 },
 {
 icon: '💰',
 questionKey: 'orders',
 color: 'green'
 },
 {
 icon: '🛠️',
 questionKey: 'usage',
 color: 'purple'
 },
 {
 icon: '🔄',
 questionKey: 'refund',
 color: 'red'
 },
 {
 icon: '🎯',
 questionKey: 'choose',
 color: 'purple'
 }
 ];

 return (
 <div className="min-h-screen bg-cream-50">
 {/* Hero区域 - 减小高度 */}
 <section className="relative overflow-hidden bg-gradient-to-br from-accent-500 to-accent-600">
 {/* 装饰性背景 */}
 <div className="absolute inset-0">
 <div className="absolute top-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
 <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-300 opacity-20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
 </div>

 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
 <div className="text-center">
 {/* 顶部标签 */}
 <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 text-white font-semibold mb-6 shadow-lg">
 <Sparkles className="w-5 h-5 mr-2" />
 {t('support.hero.badge')}
 </div>
 
 {/* 主标题 */}
 <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
 {t('support.hero.title')}
 </h1>
 
 {/* 副标题 */}
 <p className="text-lg md:text-xl text-accent-100 max-w-2xl mx-auto leading-relaxed">
 {t('support.hero.subtitle')}
 </p>
 </div>
 </div>
 </section>

 {/* 联系方式区域 - 两列布局 */}
 <section className="py-16 lg:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* 区块标题 */}
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-100 rounded-full px-4 py-2 text-sm font-semibold text-accent-600 mb-4">
 <MessageCircle className="w-4 h-4 mr-2" />
 {t('support.contact.badge')}
 </div>
 <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-4">
 {t('support.contact.title')}
 </h2>
 <p className="text-lg text-ink-600 max-w-2xl mx-auto">
 {t('support.contact.subtitle')}
 </p>
 </div>

 {/* 联系卡片网格 - 三列布局 */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
 {/* 邮件支持卡片 */}
 <div className="group bg-cream-50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-8 border border-ink-200 hover:border-ink-900 hover:-translate-y-1">
 <div className="flex flex-col h-full">
 <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 shadow-sm">
 <Mail className="w-8 h-8 text-white" />
 </div>
 <h3 className="text-2xl font-bold text-ink-900 mb-3">{t('support.contact.email.title')}</h3>
 <p className="text-ink-600 mb-6 flex-grow">
 {t('support.contact.email.description')}
 </p>
 <div className="space-y-4">
 <div className="bg-cream-100 rounded-xl p-4">
 <p className="text-sm text-ink-500 mb-1">{t('support.contact.email.label')}</p>
 <p className="font-semibold text-ink-900">{t('support.contact.email.address')}</p>
 </div>
 <div className="flex items-center text-sm text-ink-600">
 <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
 {t('support.contact.email.response')}
 </div>
 <a 
 href={`mailto:${t('support.contact.email.address')}?subject=AI工具支持咨询`}
 className="inline-flex items-center justify-center w-full bg-accent-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-accent-600 transition-colors duration-200"
 >
 <Send className="w-4 h-4 mr-2" />
 {t('support.contact.email.button')}
 </a>
 </div>
 </div>
 </div>

 {/* 在线客服卡片 - 引导用户使用右下角插件 */}
 <div className="group bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-8 text-white hover:-translate-y-1 relative overflow-hidden">
 {/* 推荐标签 */}
 <div className="absolute top-4 right-4 bg-cream-50 text-accent-600 rounded-full px-3 py-1 text-xs font-bold shadow-sm">
 {t('support.contact.chat.recommended')}
 </div>

 {/* 装饰性背景 */}
 <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>

 <div className="flex flex-col h-full relative">
 <div className="w-16 h-16 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 shadow-lg">
 <MessageCircle className="w-8 h-8 text-white" />
 </div>
 <h3 className="text-2xl font-bold text-white mb-3">{t('support.contact.chat.title')}</h3>
 <p className="text-accent-100 mb-6 flex-grow">
 {t('support.contact.chat.description')}
 </p>
 <div className="space-y-4">
 {/* 客服插件位置示意 */}
 <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4 relative">
 <p className="text-sm text-accent-100 mb-2">{t('support.contact.chat.position')}</p>
 <div className="flex items-center justify-between">
 <p className="font-semibold">{t('support.contact.chat.location')}</p>
 <div className="relative">
 <div className="w-12 h-12 bg-white bg-opacity-30 rounded-full flex items-center justify-center animate-pulse">
 <MessageCircle className="w-6 h-6 text-white" />
 </div>
 <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
 </div>
 </div>
 </div>
 <div className="flex items-center text-sm">
 <CheckCircle className="w-4 h-4 text-white mr-2" />
 {t('support.contact.chat.status')}
 </div>
 <div className="bg-cream-50 text-accent-500 px-6 py-3 rounded-xl text-center">
 <p className="font-semibold mb-1">{t('support.contact.chat.hint')}</p>
 <p className="text-sm text-ink-600">{t('support.contact.chat.instruction')}</p>
 </div>
 </div>
 </div>
 </div>

 {/* Telegram 客服卡片 */}
 <div className="group bg-cream-50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-8 border border-ink-200 hover:border-ink-900 hover:-translate-y-1">
 <div className="flex flex-col h-full">
 <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 shadow-sm">
 <Send className="w-8 h-8 text-white" />
 </div>
 <h3 className="text-2xl font-bold text-ink-900 mb-3">{t('support.contact.telegram.title')}</h3>
 <p className="text-ink-600 mb-6 flex-grow">
 {t('support.contact.telegram.description')}
 </p>
 <div className="space-y-4">
 <div className="bg-cream-100 rounded-xl p-4">
 <p className="text-sm text-ink-500 mb-1">{t('support.contact.telegram.label')}</p>
 <p className="font-semibold text-ink-900">{t('support.contact.telegram.handle')}</p>
 </div>
 <div className="flex items-center text-sm text-ink-600">
 <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
 {t('support.contact.telegram.response')}
 </div>
 <a
 href="https://t.me/aimonkey99"
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center justify-center w-full bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors duration-200"
 >
 <Send className="w-4 h-4 mr-2" />
 {t('support.contact.telegram.button')}
 </a>
 </div>
 </div>
 </div>
 </div>

 {/* 服务承诺 */}
 <div className="mt-12 bg-accent-100 rounded-xl p-8 lg:p-10">
 <div className="max-w-4xl mx-auto text-center">
 <h3 className="text-2xl font-bold text-ink-900 mb-6">{t('support.promise.title')}</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 <div className="flex flex-col items-center">
 <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mb-3">
 <CheckCircle className="w-6 h-6 text-white" />
 </div>
 <h4 className="font-semibold text-ink-900 mb-1">{t('support.promise.fast.title')}</h4>
 <p className="text-sm text-ink-600">{t('support.promise.fast.description')}</p>
 </div>
 <div className="flex flex-col items-center">
 <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mb-3">
 <Users className="w-6 h-6 text-white" />
 </div>
 <h4 className="font-semibold text-ink-900 mb-1">{t('support.promise.professional.title')}</h4>
 <p className="text-sm text-ink-600">{t('support.promise.professional.description')}</p>
 </div>
 <div className="flex flex-col items-center">
 <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center mb-3">
 <Sparkles className="w-6 h-6 text-white" />
 </div>
 <h4 className="font-semibold text-ink-900 mb-1">{t('support.promise.caring.title')}</h4>
 <p className="text-sm text-ink-600">{t('support.promise.caring.description')}</p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* 常见问题区域 - 交互式设计 */}
 <section className="py-16 lg:py-20 bg-cream-100">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* 区块标题 */}
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-100 rounded-full px-4 py-2 text-sm font-semibold text-accent-600 mb-4">
 <HelpCircle className="w-4 h-4 mr-2" />
 {t('support.faq.badge')}
 </div>
 <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-4">
 {t('support.faq.title')}
 </h2>
 <p className="text-lg text-ink-600 max-w-2xl mx-auto">
 {t('support.faq.subtitle')}
 </p>
 </div>

 {/* FAQ列表 - 手风琴样式 */}
 <div className="max-w-3xl mx-auto space-y-4">
 {faqData.map((faq, index) => (
 <div 
 key={index}
 className={`bg-cream-50 rounded-xl border ${selectedFaq === index ? 'border-accent-500 ring-1 ring-accent-500' : 'border-ink-200'} transition-colors duration-200`}
 >
 <button
 onClick={() => setSelectedFaq(selectedFaq === index ? null : index)}
 className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-cream-100 transition-colors duration-200 rounded-xl"
 >
 <div className="flex items-center gap-4">
 <span className="text-2xl">{faq.icon}</span>
 <h3 className="font-semibold text-ink-900 text-lg">{t(`support.faq.questions.${faq.questionKey}.question`)}</h3>
 </div>
 <ArrowRight className={`w-5 h-5 text-ink-400 transition-transform duration-300 ${selectedFaq === index ? 'rotate-90' : ''}`} />
 </button>
 
 {selectedFaq === index && (
 <div className="px-6 pb-5">
 <div className="bg-cream-100 rounded-xl p-4">
 <p className="text-ink-700 leading-relaxed">{t(`support.faq.questions.${faq.questionKey}.answer`)}</p>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>

 {/* 还有疑问？ */}
 <div className="mt-12 text-center">
 <div className="bg-accent-50 rounded-xl p-8 lg:p-10 border border-accent-200">
 <h3 className="text-2xl font-bold text-ink-900 mb-3">{t('support.faq.moreQuestions.title')}</h3>
 <p className="text-ink-600 mb-6">{t('support.faq.moreQuestions.description')}</p>
 <div className="inline-flex items-center bg-accent-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-accent-600 transition-colors duration-200 shadow-sm hover:shadow-md">
 <MessageCircle className="w-5 h-5 mr-2" />
 {t('support.faq.moreQuestions.button')}
 </div>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
} 